import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft, FolderOpen, Archive, Check, RotateCcw, Trash2, CalendarDays } from 'lucide-react';
import { ProfessionalDashboardSchil } from '../components/ProfessionalDashboardSchil';
import { TodoBalk } from '../components/professionalDashboard/TodoBalk';
import { useOpmaak, vandaagAmsterdam } from '../components/professionalDashboard/opmaak';
import { dashboardApi, DASHBOARD_KEY, TODO_KEY, type PersoonlijkeTodo } from '../services/professionalDashboardApi';

type Tab = 'open' | 'voltooid';

/**
 * Dashboard › Mijn actiepunten: het volledige archief van eigen actiepunten.
 * Tab 1 = openstaand (ook wat pas later gepland staat), tab 2 = afgerond, met
 * de afrondingsdatum voor de eigen verantwoording.
 */
const PersonalTodoArchive: React.FC = () => {
  const { t } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const opmaak = useOpmaak();
  const [tab, setTab] = useState<Tab>('open');
  const { data, isLoading, isError } = useQuery({ queryKey: TODO_KEY, queryFn: dashboardApi.todos });

  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: TODO_KEY });
    queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
  };
  const wijzig = useMutation({
    mutationFn: (v: { id: string; wijziging: { voltooid?: boolean; gepland_op?: string | null } }) => dashboardApi.wijzigTodo(v.id, v.wijziging),
    onSuccess: ververs,
    onError: () => toast.error(t('todo.fout')),
  });
  const verwijder = useMutation({
    mutationFn: (id: string) => dashboardApi.verwijderTodo(id),
    onSuccess: ververs,
    onError: () => toast.error(t('todo.fout')),
  });

  const vandaag = vandaagAmsterdam();
  const lijst: PersoonlijkeTodo[] = (tab === 'open' ? data?.open : data?.voltooid) ?? [];

  const tabKnop = (id: Tab, icon: React.ReactNode, label: string, aantal: number) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium ${
        tab === id ? 'border-navy-900 text-navy-900' : 'border-transparent text-navy-500 hover:text-navy-800'
      }`}
    >
      {icon}
      {label}
      <span className="rounded-full bg-navy-50 px-1.5 text-xs font-semibold text-navy-700">{aantal}</span>
    </button>
  );

  return (
    <ProfessionalDashboardSchil
      titel={t('archief.titel')}
      ondertitel={t('archief.ondertitel')}
      acties={
        <Link to="/professional/dashboard" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-800 hover:bg-navy-50">
          <ArrowLeft className="w-4 h-4" />
          {t('archief.terug')}
        </Link>
      }
    >
      <div className="p-4 sm:p-6 space-y-5">
        <TodoBalk archiefLink={false} />

        <div className="rounded-2xl border border-gray-200 bg-white shadow-card">
          <div role="tablist" className="flex flex-wrap gap-1 border-b border-gray-100 px-3">
            {tabKnop('open', <FolderOpen className="w-4 h-4" />, t('archief.tabOpen'), data?.open.length ?? 0)}
            {tabKnop('voltooid', <Archive className="w-4 h-4" />, t('archief.tabVoltooid'), data?.voltooid.length ?? 0)}
          </div>

          <div className="p-3" role="tabpanel">
            {isError && <p className="p-3 text-sm text-red-600">{t('laadFout')}</p>}
            {isLoading ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-14 rounded-lg bg-gray-50 animate-pulse" />
                ))}
              </div>
            ) : lijst.length === 0 ? (
              <p className="p-6 text-center text-sm text-navy-500">{t(tab === 'open' ? 'archief.leegOpen' : 'archief.leegVoltooid')}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {lijst.map((todo) => {
                  const status =
                    todo.voltooid || !todo.gepland_op
                      ? null
                      : todo.gepland_op < vandaag
                        ? { tekst: t('archief.verlopen'), klasse: 'bg-red-50 text-red-700' }
                        : todo.gepland_op === vandaag
                          ? { tekst: t('archief.vandaag'), klasse: 'bg-gold-100 text-gold-800' }
                          : null;
                  return (
                    <li key={todo.id} className="flex items-start gap-3 px-2 py-3">
                      <button
                        type="button"
                        onClick={() => wijzig.mutate({ id: todo.id, wijziging: { voltooid: !todo.voltooid } })}
                        disabled={wijzig.isPending}
                        aria-label={todo.voltooid ? t('archief.heropenen') : t('archief.voltooid')}
                        title={todo.voltooid ? t('archief.heropenen') : t('archief.voltooid')}
                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          todo.voltooid ? 'border-green-600 bg-green-600 text-white' : 'border-navy-300 hover:border-green-600 hover:bg-green-50'
                        }`}
                      >
                        {todo.voltooid && <Check className="w-3.5 h-3.5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${todo.voltooid ? 'text-navy-500 line-through' : 'text-navy-900'}`}>{todo.tekst}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-navy-400">
                          {todo.voltooid && todo.voltooid_op ? (
                            <span className="font-medium text-green-700">{t('archief.afgerondOp', { datum: opmaak.datumTijd(todo.voltooid_op) })}</span>
                          ) : (
                            <span>{todo.gepland_op ? t('archief.gepland', { datum: opmaak.dag(todo.gepland_op) }) : t('archief.ongepland')}</span>
                          )}
                          <span>{t('archief.aangemaaktOp', { datum: opmaak.dag(new Date(todo.created_at).toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' })) })}</span>
                          {status && <span className={`rounded-full px-1.5 py-0.5 font-semibold ${status.klasse}`}>{status.tekst}</span>}
                        </p>
                      </div>
                      {!todo.voltooid && (
                        <label className="relative inline-flex items-center p-1.5 text-navy-400 hover:text-navy-900 cursor-pointer" title={t('archief.datumWijzigen')}>
                          <CalendarDays className="w-4 h-4" />
                          <span className="sr-only">{t('archief.datumWijzigen')}</span>
                          <input
                            type="date"
                            value={todo.gepland_op ?? ''}
                            onChange={(e) => wijzig.mutate({ id: todo.id, wijziging: { gepland_op: e.target.value || null } })}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                          />
                        </label>
                      )}
                      {todo.voltooid && (
                        <button
                          type="button"
                          onClick={() => wijzig.mutate({ id: todo.id, wijziging: { voltooid: false } })}
                          className="p-1.5 text-navy-400 hover:text-navy-900"
                          title={t('archief.heropenen')}
                          aria-label={t('archief.heropenen')}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => window.confirm(t('archief.verwijderVraag')) && verwijder.mutate(todo.id)}
                        className="p-1.5 text-navy-400 hover:text-red-600"
                        title={t('archief.verwijderen')}
                        aria-label={t('archief.verwijderen')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </ProfessionalDashboardSchil>
  );
};

export default PersonalTodoArchive;
